import React from 'react';
import { capitalize } from 'lodash';
import { Flex, Tag } from 'core/components';
import { parseLegacyAsn } from '@kentik/ui-shared/synthetics/bgpLegacy';
import ASNRenderer from 'app/components/asn/ASNRenderer';

/*
  Detail rows and table values only a BGP alarm recorded before ktrac compiled AlertState.Evidence
  has. Those alarms name flagged ASNs as flat 'AS####' lists with no reason and no vantage point
  weight behind them, and their one-line finding is the prose getTestAlarms.js assembles rather
  than the sentence ktrac compiles.

  Every export returns nothing for an alarm carrying evidence, so a caller needs no branch of its
  own and this whole path is deleted by removing the file and its four call sites.
*/

// renders 'AS####' strings through ASNRenderer, falling back to a tag for anything unparseable
function asnList(values) {
  if (!Array.isArray(values) || values.length === 0) {
    return null;
  }

  return (
    <Flex flexWrap="wrap" gap={1}>
      {values.map((value) => {
        const asn = parseLegacyAsn(value);

        return asn == null ? (
          <Tag key={value} minimal>
            {value}
          </Tag>
        ) : (
          <ASNRenderer key={value} asn={asn} showLinks showNameAsLink maxWidth="100%" />
        );
      })}
    </Flex>
  );
}

/*
  Returns nothing for an alarm carrying evidence, so the caller needs no branch of its own. The
  'legacy' marker is set by summarizeLegacyBgpContext, which is the only producer of these fields.
*/
export function legacyImpactFields(bgp = {}) {
  if (!bgp.legacy) {
    return [];
  }

  const { reason, vpCount, totalVps, rpkiStatus, badOrigins, badUpstreams } = bgp;

  return [
    // reason is prose standing in for an alarm whose event view model carried no statistics at all
    { label: 'Details', value: vpCount == null ? reason || null : null },
    // the event view model counts these apart, so a legacy alarm cannot state them as one fact
    { label: 'Total VPs', value: totalVps ?? null },
    { label: 'RPKI Status', value: rpkiStatus ? capitalize(rpkiStatus) : null },
    { label: 'Invalid Origins', value: asnList(badOrigins) },
    { label: 'Invalid Upstreams', value: asnList(badUpstreams) }
  ];
}

/*
  Stands in for the compiled finding. getTestAlarms.js assembles this from the VP counts alone --
  '104 vantage points affected' -- so it states the alarm's magnitude and nothing about its cause.
*/
export function legacyFinding(bgp = {}) {
  return bgp.legacy ? bgp.reason || null : null;
}

// the flagged ASNs a legacy alarm carries, so a search for 'AS3356' finds it as it would a new one
export function legacyFilterValues(bgp = {}) {
  if (!bgp.legacy) {
    return [];
  }

  return [bgp.reason, ...(bgp.badOrigins || []), ...(bgp.badUpstreams || [])];
}

/*
  The flagged ASN rows in the shape summarizeBgpEvidence produces, so the incident log column reads
  one list. These alarms record what an ASN did wrong but not how many vantage points saw it, so
  the rows carry a role and no vpCount, and their order is the order the alarm listed them.
*/
export function legacyFlaggedAsns(bgp = {}) {
  if (!bgp.legacy) {
    return [];
  }

  return [
    ...(bgp.badOrigins || []).map((value) => ({
      asn: parseLegacyAsn(value),
      roleLabel: 'invalid origin',
      roleQualifier: null
    })),
    ...(bgp.badUpstreams || []).map((value) => ({
      asn: parseLegacyAsn(value),
      roleLabel: 'invalid upstream',
      roleQualifier: null
    }))
  ].filter((row) => row.asn !== null);
}
